import SectionCard from '@/components/base/SectionCard';
import Badge from '@/components/base/Badge';
import MarketSelect from '@/components/base/MarketSelect';
import { brandCurrencies } from '@/mocks/branding';
import { currenciesForMarkets } from '@/utils/markets';
import type { BrandProfile } from '@/types/portal';

interface AgencyProfileCardProps {
  profile: BrandProfile;
  canEdit: boolean;
  onField: (key: keyof BrandProfile, value: string) => void;
  onSetMarkets: (markets: string[]) => void;
}

const fieldClass =
  'h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-background-100 disabled:text-foreground-500';

const labelClass = 'mb-1.5 block text-xs font-label font-medium text-foreground-700';

export default function AgencyProfileCard({
  profile,
  canEdit,
  onField,
  onSetMarkets,
}: AgencyProfileCardProps) {
  const marketCurrencies = currenciesForMarkets(profile.markets);
  const currencyOptions = Array.from(
    new Set([
      ...(marketCurrencies.length > 0 ? marketCurrencies : brandCurrencies),
      profile.baseCurrency,
    ]),
  );

  return (
    <SectionCard
      title="Agency profile"
      subtitle="The name, voice and contact details shown on every branded surface"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="brand-name" className={labelClass}>
            Display name
          </label>
          <input
            id="brand-name"
            type="text"
            value={profile.name}
            disabled={!canEdit}
            onChange={(event) => onField('name', event.target.value)}
            placeholder="e.g. Kili Creatives"
            className={fieldClass}
          />
        </div>

        <div>
          <label htmlFor="brand-legal" className={labelClass}>
            Legal entity
          </label>
          <input
            id="brand-legal"
            type="text"
            value={profile.legalName}
            disabled={!canEdit}
            onChange={(event) => onField('legalName', event.target.value)}
            placeholder="e.g. Kili Creatives Limited"
            className={fieldClass}
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="brand-tagline" className={labelClass}>
            Tagline
          </label>
          <input
            id="brand-tagline"
            type="text"
            value={profile.tagline}
            disabled={!canEdit}
            onChange={(event) => onField('tagline', event.target.value)}
            placeholder="A short line that captures what you do"
            className={fieldClass}
          />
        </div>

        <div>
          <label htmlFor="brand-email" className={labelClass}>
            Support email
          </label>
          <input
            id="brand-email"
            type="email"
            value={profile.supportEmail}
            disabled={!canEdit}
            onChange={(event) => onField('supportEmail', event.target.value)}
            placeholder="hello@youragency.com"
            className={fieldClass}
          />
        </div>

        <div>
          <label htmlFor="brand-website" className={labelClass}>
            Website
          </label>
          <input
            id="brand-website"
            type="url"
            value={profile.website}
            disabled={!canEdit}
            onChange={(event) => onField('website', event.target.value)}
            placeholder="https://youragency.com"
            className={fieldClass}
          />
        </div>

        <div>
          <label htmlFor="brand-currency" className={labelClass}>
            Base currency
          </label>
          <div className="relative">
            <select
              id="brand-currency"
              value={profile.baseCurrency}
              disabled={!canEdit}
              onChange={(event) => onField('baseCurrency', event.target.value)}
              className="h-10 w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 pl-3 pr-8 text-sm font-label text-foreground-800 focus:border-primary-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-background-100 disabled:text-foreground-500"
            >
              {currencyOptions.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500" />
          </div>
        </div>

        <div>
          <label htmlFor="brand-initials" className={labelClass}>
            Monogram initials
          </label>
          <input
            id="brand-initials"
            type="text"
            value={profile.logoInitials}
            disabled={!canEdit}
            maxLength={3}
            onChange={(event) => onField('logoInitials', event.target.value.toUpperCase())}
            placeholder="KC"
            className={`${fieldClass} uppercase`}
          />
        </div>

        <div className="sm:col-span-2">
          <p className={labelClass}>Operating markets</p>
          <MarketSelect
            value={profile.markets}
            onChange={onSetMarkets}
            disabled={!canEdit}
            placeholder="Search African countries…"
          />
          <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-foreground-500">
            <i className="ri-information-line" />
            Markets shape the currencies, rails and creators available to your team.
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-background-100 pt-4">
        <Badge tone="secondary" icon="ri-translate-2">
          Locale inherited
        </Badge>
        <Badge tone="outline" icon="ri-map-pin-line">
          {profile.markets.length} market{profile.markets.length === 1 ? '' : 's'}
        </Badge>
        <Badge tone="outline" icon="ri-money-dollar-circle-line">
          Base {profile.baseCurrency}
        </Badge>
      </div>
    </SectionCard>
  );
}